<template>
  <AuthShell>
    <!-- Step 1: request a magic link -->
    <template v-if="state === 'enter'">
      <h1 class="auth-title">Guest sign in</h1>
      <p class="auth-sub">
        Invited to collaborate on a project? Enter the email you were invited
        with and we'll send you a one-time sign-in link.
      </p>
      <form @submit.prevent="requestLink">
        <label class="auth-field">
          <span class="auth-label">Email address</span>
          <input
            v-model="email"
            class="auth-input"
            type="email"
            autocomplete="email"
            placeholder="you@company.com"
            :disabled="loading"
          />
        </label>
        <button class="auth-btn" type="submit" :disabled="loading">
          {{ loading ? "Sending…" : "Email me a sign-in link" }}
        </button>
      </form>
    </template>

    <!-- Step 2: link sent -->
    <template v-else-if="state === 'sent'">
      <h1 class="auth-title">Check your email</h1>
      <p class="auth-sub">
        We sent a sign-in link to <strong>{{ email }}</strong
        >. Open it in this browser to continue — it works once and expires
        shortly.
      </p>
      <button
        class="auth-btn auth-btn--ghost"
        type="button"
        @click="state = 'enter'"
      >
        Use a different email
      </button>
    </template>

    <!-- Return leg: completing the link sign-in -->
    <template v-else-if="state === 'completing'">
      <div class="auth-center">
        <span class="auth-spinner" />
        <p class="auth-sub" style="margin: 0">Signing you in…</p>
      </div>
    </template>

    <!-- Return leg on a different browser: confirm the email -->
    <template v-else-if="state === 'confirm'">
      <h1 class="auth-title">Confirm your email</h1>
      <p class="auth-sub">
        To finish signing in, confirm the email address the link was sent to.
      </p>
      <form @submit.prevent="completeSignIn(email)">
        <label class="auth-field">
          <span class="auth-label">Email address</span>
          <input
            v-model="email"
            class="auth-input"
            type="email"
            autocomplete="email"
            placeholder="you@company.com"
            :disabled="loading"
          />
        </label>
        <button class="auth-btn" type="submit" :disabled="loading">
          Continue
        </button>
      </form>
    </template>

    <!-- First run: ask for a display name so teammates see who's talking -->
    <template v-else-if="state === 'name'">
      <h1 class="auth-title">Welcome to Alice</h1>
      <p class="auth-sub">
        What's your name? It's shown to the team when you chat and add notes.
      </p>
      <form @submit.prevent="saveName">
        <label class="auth-field">
          <span class="auth-label">Your name</span>
          <input
            v-model="displayName"
            class="auth-input"
            type="text"
            autocomplete="name"
            placeholder="Jane Researcher"
            :disabled="loading"
          />
        </label>
        <button
          class="auth-btn"
          type="submit"
          :disabled="loading || !displayName.trim()"
        >
          Continue to the project
        </button>
      </form>
    </template>

    <p v-if="error" class="auth-error">{{ error }}</p>

    <template #footer>
      <div class="auth-foot-stack">
        <span>Powered by Braingineers</span>
        <span class="auth-foot-alt">
          Work at Valsplat or Braingineers?
          <router-link class="auth-link" to="/signin"
            >Sign in with Google</router-link
          >
        </span>
      </div>
    </template>
  </AuthShell>
</template>

<script>
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/firebase";
import AuthShell from "@/components/AuthShell.vue";

const EMAIL_STORAGE_KEY = "guestEmailForSignIn";

export default {
  name: "GuestSignIn",
  components: { AuthShell },
  data() {
    return {
      state: "enter",
      email: "",
      displayName: "",
      loading: false,
      error: null,
    };
  },
  async mounted() {
    // Return leg: the user clicked the link in their email. Same-browser
    // flows find the email in localStorage; a different browser asks for it.
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const stored = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      if (stored) {
        this.email = stored;
        await this.completeSignIn(stored);
      } else {
        this.state = "confirm";
      }
      return;
    }
    // Already signed in (e.g. reload): straight into the project.
    if (auth.currentUser) {
      await this.enterProject();
    }
  },
  methods: {
    async requestLink() {
      const email = this.email.trim().toLowerCase();
      if (!email) return;
      this.loading = true;
      this.error = null;
      try {
        // Only emails with a live invite get a link (server-checked).
        await httpsCallable(functions, "requestGuestSignIn")({ email });
        await sendSignInLinkToEmail(auth, email, {
          url: `${window.location.origin}/guest`,
          handleCodeInApp: true,
        });
        window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
        this.state = "sent";
      } catch (err) {
        console.error(err);
        this.error =
          err.code === "functions/not-found"
            ? "No active invite for this email address. Ask your Valsplat contact to (re)invite you."
            : "Couldn't send the sign-in link. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async completeSignIn(email) {
      this.loading = true;
      this.error = null;
      this.state = "completing";
      try {
        const result = await signInWithEmailLink(
          auth,
          email.trim().toLowerCase(),
          window.location.href
        );
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
        if (!result.user.displayName) {
          this.state = "name";
        } else {
          await this.enterProject();
        }
      } catch (err) {
        console.error(err);
        this.state = "enter";
        this.error =
          "This sign-in link is invalid or has expired — request a new one below.";
      } finally {
        this.loading = false;
      }
    },
    async saveName() {
      const name = this.displayName.trim();
      if (!name || !auth.currentUser) return;
      this.loading = true;
      this.error = null;
      try {
        await updateProfile(auth.currentUser, { displayName: name });
        // The users doc is provisioned by a server trigger; merge in case
        // this write wins the race.
        await setDoc(
          doc(db, "users", auth.currentUser.uid),
          { name, uid: auth.currentUser.uid, email: auth.currentUser.email },
          { merge: true }
        );
        await this.enterProject();
      } catch (err) {
        console.error(err);
        this.error = "Couldn't save your name. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    enterProject() {
      // Guests may hold grants to several projects — land on the list.
      this.$router.push("/projects");
    },
  },
};
</script>

<style scoped>
.auth-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 34px;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--vp-black);
  margin: 0 0 10px;
}
.auth-sub {
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-3);
  line-height: 1.5;
  margin: 0 0 28px;
}
.auth-sub strong {
  color: var(--vp-black);
}
.auth-field {
  display: block;
  margin-bottom: 20px;
}
.auth-label {
  display: block;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.auth-input {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--vp-black);
  transition: border-color var(--dur-fast) var(--ease-hover);
}
.auth-input:focus {
  outline: none;
  border-color: var(--vp-black);
}
.auth-input:disabled {
  background: var(--bg-muted);
}
.auth-btn {
  width: 100%;
  height: 48px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}
.auth-btn:hover:not(:disabled) {
  background: var(--accent-100);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.auth-btn:active:not(:disabled) {
  transform: scale(0.99);
}
.auth-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.auth-btn--ghost {
  background: var(--vp-white);
  color: var(--vp-black);
}
.auth-btn--ghost:hover:not(:disabled) {
  background: var(--vp-black);
  border-color: var(--vp-black);
  color: var(--vp-white);
}
.auth-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
}
.auth-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-hairline);
  border-top-color: var(--accent-100);
  border-radius: 50%;
  animation: auth-spin 0.7s linear infinite;
}
@keyframes auth-spin {
  to {
    transform: rotate(360deg);
  }
}
.auth-error {
  margin: 20px 0 0;
  padding: 12px 14px;
  background: var(--vp-rood-10);
  border-left: 3px solid var(--vp-rood-100);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--vp-rood-140);
}
.auth-foot-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.auth-foot-alt {
  font-size: 12px;
}
.auth-link {
  color: var(--accent-160);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.auth-link:hover {
  color: var(--vp-black);
}
</style>
